import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type TabSyncConfirmKind = 'login-same-account' | 'login-other-account' | 'login-link' | 'switch-school' | 'logout';

export interface TabSyncConfirm {
  kind: TabSyncConfirmKind;
  resolve: (confirmed: boolean) => void;
}

/**
 * Keeps open tabs in step with the session stored in localStorage.
 *
 * localStorage is shared by every tab, so a login, logout or school switch in
 * one tab silently changes the token / school used by all the others. The
 * browser fires a `storage` event in every OTHER tab when localStorage changes
 * (never in the tab that wrote it), so we listen here and:
 *  - logout / different account in another tab -> send this tab to /login
 *  - same account, new token / school / role    -> reload this tab
 *
 * Before the acting tab makes such a change it asks the user to confirm
 * (confirmLogin / confirmSchoolSwitch / confirmLogout), but only when other
 * signed-in tabs are actually open. Open tabs are found with a BroadcastChannel
 * ping; without BroadcastChannel support we skip the popup and still sync.
 *
 * A website cannot close tabs, so redirect / reload is the strongest action
 * available. Unsaved input in the affected tabs is lost by design.
 */
@Injectable({
  providedIn: 'root'
})
export class TabSyncService {
  private readonly watchedKeys = ['token', 'userId', 'loginAs', 'schoolId'];

  // login writes ~10 keys one after another; act once after they settle
  private readonly settleMs = 300;
  private timer: any = null;
  private accountChanged = false;

  // how long to wait for other tabs to answer a presence ping
  private readonly pingTimeoutMs = 200;
  private channel: BroadcastChannel | null = null;

  // the dialog component (app.component.html) renders whatever is pending here
  confirm$ = new BehaviorSubject<TabSyncConfirm | null>(null);

  constructor(private zone: NgZone) {
    // no change detection needed for the listeners themselves
    this.zone.runOutsideAngular(() => {
      window.addEventListener('storage', (event: StorageEvent) => this.onStorage(event));

      if (typeof BroadcastChannel !== 'undefined') {
        this.channel = new BroadcastChannel('blueride-tabs');
        this.channel.onmessage = (msg: MessageEvent) => {
          // answer only when this tab is signed in, a tab on the login page has nothing to lose
          if (msg.data?.type === 'ping' && !this.isOnLoginPage() && localStorage.getItem('token')) {
            this.channel.postMessage({ type: 'pong', id: msg.data.id });
          }
        };
      }
    });
  }

  /** Call before writing a new login to localStorage, with the user id from the login response. */
  async confirmLogin(newUserId: string | number): Promise<boolean> {
    if (!(await this.hasOtherSignedInTabs())) return true;
    const sameAccount = localStorage.getItem('userId') === String(newUserId);
    return this.ask(sameAccount ? 'login-same-account' : 'login-other-account');
  }

  /** Call before a ?token= login link clears the session, the account isn't known yet at that point. */
  async confirmLink(): Promise<boolean> {
    if (!(await this.hasOtherSignedInTabs())) return true;
    return this.ask('login-link');
  }

  async confirmSchoolSwitch(): Promise<boolean> {
    if (!(await this.hasOtherSignedInTabs())) return true;
    return this.ask('switch-school');
  }

  async confirmLogout(): Promise<boolean> {
    if (!(await this.hasOtherSignedInTabs())) return true;
    return this.ask('logout');
  }

  private ask(kind: TabSyncConfirmKind): Promise<boolean> {
    return new Promise(resolve => {
      this.zone.run(() => this.confirm$.next({
        kind,
        resolve: (confirmed: boolean) => {
          this.confirm$.next(null);
          resolve(confirmed);
        }
      }));
    });
  }

  private hasOtherSignedInTabs(): Promise<boolean> {
    if (!this.channel) return Promise.resolve(false);

    // a BroadcastChannel never receives its own messages, so only other tabs answer
    const id = `${Date.now()}-${Math.random()}`;
    return new Promise(resolve => {
      const onPong = (msg: MessageEvent) => {
        if (msg.data?.type === 'pong' && msg.data.id === id) done(true);
      };
      const done = (found: boolean) => {
        clearTimeout(timeout);
        this.channel.removeEventListener('message', onPong);
        resolve(found);
      };
      const timeout = setTimeout(() => done(false), this.pingTimeoutMs);
      this.channel.addEventListener('message', onPong);
      this.channel.postMessage({ type: 'ping', id });
    });
  }

  private onStorage(event: StorageEvent): void {
    if (event.storageArea !== localStorage) return;

    // key === null means localStorage.clear() (logout / ?token= login)
    const cleared = event.key === null;
    if (!cleared && !this.watchedKeys.includes(event.key)) return;
    if (!cleared && event.oldValue === event.newValue) return;

    if (cleared || (event.key === 'userId' && event.oldValue !== event.newValue)) {
      this.accountChanged = true;
    }

    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.apply(), this.settleMs);
  }

  private apply(): void {
    const accountChanged = this.accountChanged;
    this.accountChanged = false;

    // a tab already on the login page has no session to protect
    if (this.isOnLoginPage()) return;

    if (accountChanged || !localStorage.getItem('token')) {
      // don't call UserService.logOut(): its localStorage.clear() would wipe
      // the session the other tab just created
      window.location.replace('/login');
      return;
    }

    // same account, but token / school / role changed: pick up the new context
    window.location.reload();
  }

  private isOnLoginPage(): boolean {
    return window.location.pathname.startsWith('/login');
  }
}
